import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { StatusBadge, DetailField } from "./withdrawalUtils";

const requestId = (request) => request?.id || request?._id;

const resourcePersonName = (request) =>
  request?.resourcePersonName ||
  request?.resourcePerson?.name ||
  request?.name ||
  "—";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const WithdrawalDetailModal = ({
  isOpen,
  onClose,
  request,
  onApprove,
  onReject,
}) => {
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen || !request) return null;

  const handleApprove = async () => {
    setIsLoading(true);
    await onApprove(requestId(request));
    setIsLoading(false);
    onClose();
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setIsLoading(true);
    await onReject(requestId(request), rejectReason);
    setIsLoading(false);
    setShowRejectForm(false);
    setRejectReason("");
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-xl p-7 max-w-[600px] w-[90%] max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-xl font-semibold m-0">
            Withdrawal Request
          </h3>
          <button
            onClick={onClose}
            className="bg-none border-none text-2xl cursor-pointer text-gray-400 p-1"
          >
            &times;
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <DetailField
            label="Resource Person"
            value={resourcePersonName(request)}
          />
          <DetailField
            label="Amount Requested"
            value={`₦${parseFloat(request.amount || 0).toLocaleString()}`}
          />
          <DetailField label="Status" value={<StatusBadge status={request.status} />} />
          <DetailField
            label="Requested On"
            value={formatDate(request.requestedAt || request.createdAt)}
          />
          <DetailField label="Approved On" value={formatDate(request.approvedAt)} />
          <DetailField
            label="Rejection Reason"
            value={request.rejectionReason || request.rejection_reason || "—"}
            valueColor="text-red-600"
          />
          <DetailField
            label="Failure Reason"
            value={request.failureReason || "—"}
            valueColor="text-red-600"
          />
          <DetailField label="Gateway Reference" value={request.gatewayReference || "—"} />
          <DetailField label="Transfer Code" value={request.transferCode || "—"} />
        </div>

        {request.status === "REQUESTED" && (
          <div className="border-t border-gray-200 pt-4">
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowRejectForm((value) => !value)}
                disabled={isLoading}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faTimes} /> Reject
              </button>
              <button
                type="button"
                onClick={handleApprove}
                disabled={isLoading}
                className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faCheck} /> {isLoading ? "Approving…" : "Approve"}
              </button>
            </div>

            {showRejectForm && (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  handleReject();
                }}
                className="mt-4"
              >
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Reason for rejection
                </label>
                <textarea
                  rows={3}
                  value={rejectReason}
                  onChange={(event) => setRejectReason(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-red-400"
                  required
                />
                <div className="mt-2 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowRejectForm(false)}
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || !rejectReason.trim()}
                    className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    Confirm rejection
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WithdrawalDetailModal;
